import React, { useEffect, useRef, useState } from 'react';
import './Spinner.css';
import { observer } from 'mobx-react-lite';
import etaLoader from '@/lib/EtaLoader';
import Image from 'next/image';
import { getStaticAssetPath } from '@/lib/utils';
import { ButtonShadcn } from '@/components/ui/button-shadcn';
import { useTranslation } from 'react-i18next';

const loadingIcon = getStaticAssetPath('/static/siteLogo.svg');

const Spinner = observer(() => {
  const MIN_DISPLAY_TIME = 1000;
  const { t } = useTranslation();
  const [animatedPercent, setAnimatedPercent] = useState(etaLoader.animatedPercentValue);
  // Minimum display time logic
  const [visible, setVisible] = useState(false);
  const hideTimeout = useRef<NodeJS.Timeout | null>(null);
  const lastShowTime = useRef<number>(0);

  // Show spinner when loading starts
  useEffect(() => {
    if (etaLoader.percent < 100 && etaLoader.message) {
      setVisible(true);
      lastShowTime.current = Date.now();
      if (hideTimeout.current) {
        clearTimeout(hideTimeout.current);
        hideTimeout.current = null;
      }
    } else if (visible) {
      // Wait MIN_DISPLAY_TIME after reaching 100% before hiding
      hideTimeout.current = setTimeout(() => {
        setVisible(false);
      }, MIN_DISPLAY_TIME);
    }
    // Cleanup on unmount
    return () => {
      if (hideTimeout.current) clearTimeout(hideTimeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etaLoader.percent, etaLoader.message]);
  // Smooth progress bar animation

  useEffect(() => {
    setAnimatedPercent(etaLoader.animatedPercentValue);
  }, [etaLoader.animatedPercentValue]);

  if (!visible) {
    return null;
  }

  const style: React.CSSProperties = {
    position: "fixed",
    zIndex: 10000,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)"
  };

  return (
    <div style={style} className='spinner-wrapper'>
      <div className='flex flex-col items-center justify-center gap-2 text-center'>
        <div className="spinner__text text-center">{etaLoader.message}</div>
        <div style={{ width: 80, height: 80 }} className="spinner">
          <Image
           src={loadingIcon}
           alt={etaLoader.message}
           title={etaLoader.message}
           width={65}
           height={65}
           className='object-fit-contain bounce-logo'
           style={{ margin: 10 }}
           priority={true}
          />
        </div>
        <div className="w-full mt-2">
          <div className="progress-bar-bg" style={{ 
             width: 120,
             height: 8,
             background: '#eee',
             borderRadius: 4,
             overflow: 'hidden',
             margin: '0 auto' 
            }}>
            <div
              className="progress-bar-fg"
              style={{
                width: `${animatedPercent}%`,
                height: 8,
                background: '#3b82f6',
                borderRadius: 4,
                transition: 'width 0.6s ease-in-out',
              }}
            />
          </div>
          <div className="text-xs mt-1">{Math.round(animatedPercent)}%</div>
        </div>
      </div>
    </div>
  );
});

export default Spinner;